import moment from 'moment'
import React, {useMemo, useRef} from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table'
import {CheckboxSeleccionar} from '../inputs'
import {FiltroTexto} from '../Tablas'
import {BotonSimple} from '../Botones'
import {ESTADOS_ATENCION_CONSTANTES, TEXTO_VISUAL} from '../../constantes'
import TablaConcepto from './TablaConcepto'
import {adaptarColumnas} from '../../utils/adaptarColumnasTabla'

const TablaOrdenes = ({
  data,
  columns,
  usarSelected = true,
  funcionSeleccion,
  children,
  limpiarTodosLosFiltros,
}) => {
  const filterFns = useMemo(
    () => ({
      dobleTexto: (row, columnId, filterValue) => {
        const rowValue = row.getValue(columnId)
        return rowValue !== undefined
          ? String(rowValue.Fila1)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase()) ||
              String(rowValue.Fila2)
                .toLowerCase()
                .includes(String(filterValue).toLowerCase())
          : true
      },
      fecha: (row, columnId, filterValue) => {
        const rowValue = row.getValue(columnId)
        return rowValue !== undefined
          ? moment(rowValue).format('DD/MM/YYYY') ==
              moment(filterValue).format('DD/MM/YYYY')
          : true
      },
      lista: (row, columnId, filterValue) => {
        if (!filterValue) return true
        const rowValue = row.getValue(columnId)
        return rowValue !== undefined ? rowValue == filterValue : false
      },
    }),
    []
  )

  const selectedRowsChangedKey = useRef(0)

  const columnasAdaptadas = useMemo(() => {
    const adapted = adaptarColumnas(columns)
    // Add checkbox column
    adapted.push({
      id: 'checkbox',
      meta: {
        className: `w-1/12 ${!usarSelected ? 'hidden' : ''}`,
        disableFilters: true,
      },
      enableColumnFilter: false,
      header: ({table}) => (
        <div className="flex w-full flex-wrap justify-evenly items-center">
          <span className="w-full text-center my-2">
            {'Seleccionar todo'}
          </span>
          <div className="flex w-full justify-center items-center">
            <CheckboxSeleccionar
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          </div>
        </div>
      ),
      cell: ({row}) => (
        <div className="flex justify-center items-center">
          <CheckboxSeleccionar
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
            disabled={
              row.original.estado !== TEXTO_VISUAL.ORDENES.ESTADO.ATENDIDA
            }
          />
        </div>
      ),
    })
    return adapted
  }, [columns, usarSelected])

  const table = useReactTable({
    columns: columnasAdaptadas,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    filterFns,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    initialState: {
      pagination: {pageIndex: 0, pageSize: 10},
    },
    onRowSelectionChange: () => {
      selectedRowsChangedKey.current = Date.now()
    },
  })

  const limpiarFiltros = () => {
    table.resetColumnFilters()
    funcionSeleccion(false ? data : [])
  }

  return (
    <>
      <table className="w-full">
        <thead
          className="w-full flex justify-evenly items-center bg-cendiatra-verde-7 text-white h-20 rounded-10px"
          style={{lineHeight: '16px'}}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              className="w-full flex justify-evenly items-center h-full p-3px"
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`${header.column.columnDef.meta?.className || ''} h-full bg-cendiatra-verde-3 flex flex-wrap justify-center items-center content-center  m-3px rounded-9px`}
                >
                  <div
                    className="w-full text-14px flex justify-evenly items-center font-light"
                    style={{margin: '6px 0 '}}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                  <div
                    className="w-full flex justify-evenly items-center mb-2 text-13px px-1"
                    style={{margin: '6px 0'}}
                  >
                    {header.column.getCanFilter() ? (
                      <FiltroTexto column={header.column} />
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="w-full ">
          {table.getRowModel().rows.map((row, i) => {
            return (
              <div key={row.id}>
                <tr
                  className="w-full border border-cendiatra text-cendiatra-gris-1 rounded-2xl flex justify-evenly items-center text-center text-13px my-3 py-1"
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        className={`${cell.column.columnDef.meta?.className || ''} m-3px  p-3px`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
                {row.original.filaSeleccionada &&
                row.original.servicios &&
                row.original.estadoAtencion !==
                  ESTADOS_ATENCION_CONSTANTES.EN_PROCESO ? (
                  <TablaConcepto servicios={row.original.servicios} />
                ) : null}
              </div>
            )
          })}
        </tbody>
      </table>
      <div className="w-full flex flex-wrap justify-center m-5 h-10 mb-20 ">
        {!data?.length ? (
          <div className="w-full flex justify-center items-start border border-cendiatra-verde-2 rounded-lg">
            <span className="text-cendiatra-gris-1 text-xl my-8">
              No hay resultados para la busqueda establecida. Por favor, ingrese
              un numero de documento, nombre, apellido, o establezca un rango de
              fechas para obtener resultados.
            </span>
          </div>
        ) : null}
        <div className="flex w-full justify-end">{children}</div>
        <div className="flex w-full justify-end -mt-16">
          <BotonSimple
            texto={'Limpiar filtros'}
            estilosBoton={
              false
                ? 'w-32 h-40px p-1.5 rounded-lg bg-cendiatra-gris-4 bg-cover bg-no-repeat bg-center text-white font-bold  text-14px mx-25px pointer-events-none\t'
                : 'w-32 h-40px p-1.5 rounded-lg bg-fondoBoton bg-cover bg-no-repeat bg-center text-white  font-bold text-14px mx-25px'
            }
            funcion={limpiarTodosLosFiltros}
            deshabilitado={Boolean(false)}
          />
        </div>
      </div>
    </>
  )
}

export default TablaOrdenes
